import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Box, Typography, Button, Avatar } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import assets from '../../assets';
import { useDashboardContext } from '../../contexts/DashboardContext';
import { useNavigate } from 'react-router-dom';
import colorConfigs from '../../configs/colorConfigs';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { alpha, keyframes } from '@mui/material/styles';

const attentionPulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.06);
    box-shadow: 0px 5px 18px ${alpha(colorConfigs.secondary, 0.35)},
                0px 0px 0px 4px ${alpha(colorConfigs.secondary, 0.15)};
  }
  100% {
    transform: scale(1);
  }
`;

const Header = () => {
    const { resetDashboard, userName } = useDashboardContext();
    const navigate = useNavigate();
    const [runInitialAnimation, setRunInitialAnimation] = useState(true);

    useEffect(() => {
        // Timer to disable the animation after its duration
        const animationDuration = 1200; // 1.2 seconds in milliseconds
        const timer = setTimeout(() => {
            setRunInitialAnimation(false);
        }, animationDuration);

        // Cleanup timer on component unmount
        return () => clearTimeout(timer);
    }, []); // Empty dependency array ensures this runs only once on mount

    const handleLogout = () => {
        try {
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            localStorage.removeItem('clinicianId');
            localStorage.removeItem('isLoggedIn');
            resetDashboard();
            navigate('/');
            console.log("Logged out successfully!");
        } catch (error) {
            console.error('There was an error logging out:', error);
        }
    };

    const handleDocumentation = () => {
        window.open('https://www.evolutiondevices.com/clinician-dashboard-tutorials', '_blank');
    };

    const firstName = userName ? userName.split(' ')[0] : '';

    return (
        <AppBar 
            position="static" 
            elevation={0}
            sx={{
                backgroundColor: colorConfigs.primary,
                marginBottom: '24px',
                borderRadius: 0,
            }}
        >
            <Toolbar 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    width: '100%', 
                    padding: {xs: '8px 16px', md: '10px 32px'},
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img 
                        src={assets.images.logo} 
                        alt="logo" 
                        style={{ 
                            height: '55px',
                        }} 
                    />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {userName && (
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                backgroundColor: 'rgba(255,255,255,0.3)', 
                                borderRadius: '30px',
                                padding: '6px 8px 6px 6px',
                                marginRight: '12px',
                            }}
                        >
                            <Avatar 
                                sx={{ 
                                    width: 32, 
                                    height: 32, 
                                    backgroundColor: colorConfigs.secondary,
                                    color: '#fff',
                                    fontSize: '14px',
                                    mr: 1,
                                }}
                            >
                                {firstName.charAt(0)}
                            </Avatar>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: colorConfigs.secondary,
                                    fontWeight: '500',
                                }}
                            >
                                {userName}
                            </Typography>
                        </Box>
                    )}
                   <Button
                        variant="contained"
                        startIcon={<PlayCircleOutlineIcon />}
                        onClick={handleDocumentation}
                        sx={{
                            backgroundColor: colorConfigs.secondary,
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: '500',
                            fontSize: '0.875rem',
                            borderRadius: '10px',
                            marginRight: '12px',
                            padding: '6px 16px',
                            height: '36.5px',
                            transition: 'transform 0.15s ease-in-out, background-color 0.15s ease-in-out',

                            // Conditionally apply the animation based on the state
                            // No 'forwards' needed here as JS controls the removal of the animation rule
                            animation: runInitialAnimation ? `${attentionPulse} 1.2s ease-out 1` : 'none',

                            '&:hover': {
                                backgroundColor: `${alpha(colorConfigs.secondary, 0.9)}`,
                                transform: 'scale(1.03)',
                                // If runInitialAnimation is true (during the first 1.2s), and user hovers,
                                // setting animation to 'none' here is a good safeguard.
                                // If runInitialAnimation is false, the base animation is already 'none'.
                                animation: runInitialAnimation ? 'none' : undefined,
                            },
                            '&:active': {
                                transform: 'scale(0.98)',
                            }
                        }}
                    >
                        Video Tutorials
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleLogout}
                        sx={{
                            backgroundColor: colorConfigs.secondary,
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: '400',
                            fontSize: '0.875rem',
                            borderRadius: '10px',
                            minWidth: 'auto', // Keep this to allow the button to shrink to icon size + padding
                            padding: '0', // Reset padding, we'll control it for the icon
                            marginRight: '6px',
                            height: '36px', // Match the height of the "User Manual" button
                            width: '36.5px', // Make it a square or adjust as needed for visual balance
                            display: 'flex', // Enable flex for centering
                            alignItems: 'center', // Center icon vertically
                            justifyContent: 'center', // Center icon horizontally
                            '& .MuiButton-startIcon': { // Target the icon wrapper specifically
                                margin: 0, // Remove default margin if startIcon prop is used
                            },
                            '&:hover': {
                                backgroundColor: `#CC0202`,
                            }
                        }}
                    >
                        {/* Place the icon directly as a child for better centering control without startIcon */}
                        <LogoutIcon sx={{ fontSize: '1.25rem' }} />
                    </Button>
                </Box>
            </Toolbar>
        </AppBar>
    );
}

export default Header;
